import { UserDetail } from '../../../js/api';

/**
 * Permission codes are `<action>__<app_label>__<model>` strings. Org reps carry
 * the codes their AuthGroups grant; staff carry the codes their `portal_*`
 * Django groups grant (a superuser gets every code). The staff user type only
 * opens the `/staff` route tree — access inside it is decided by these codes.
 *
 * Non-hook variant of `useHasPermission` for item builders and helpers that
 * run outside React rendering.
 */
export const hasPermission = (
  user: Pick<UserDetail, 'permissions'> | null | undefined,
  permission: string
): boolean => Boolean(user?.permissions?.includes(permission));

/**
 * Model-level `list__<app>__<model>` codes, shared by staff and org users (each
 * audience gets them through its own groups). They open the menus, the list routes
 * and links into those lists (e.g. the drawer provider record links).
 */
export const LIST_PERMISSIONS = {
  providers: 'list__providers__provider',
  needs: 'list__staffing__need',
  opportunities: 'list__assignments__opportunity',
  clearances: 'list__connections__clearance',
  connections: 'list__connections__connectedfacility',
  assignments: 'list__assignments__assignment',
  timecards: 'list__assignments__timecard',
  facilities: 'list__healthcare_entities__healthcarefacility',
  // No list__ contact code exists: HCO contacts ride on the facilities list code.
  contacts: 'list__healthcare_entities__healthcarefacility',
  users: 'list__healthcare_entities__healthcareentityrep',
  imports: 'list__ingestion__ingestfile',
  bizintel: 'list__reports__report',
} as const;

/**
 * Staff sidenav / home-card visibility by item key. Admin rides on the Django
 * admin flag; BizIntel needs its portal code (per-report Django groups still
 * apply inside it); anything unlisted is always visible.
 */
export const STAFF_MENU_ITEM_PERMISSIONS: Record<string, string> = {
  providers: LIST_PERMISSIONS.providers,
  needs: LIST_PERMISSIONS.needs,
  opportunities: LIST_PERMISSIONS.opportunities,
  clearance: LIST_PERMISSIONS.clearances,
  connections: LIST_PERMISSIONS.connections,
  assignments: LIST_PERMISSIONS.assignments,
  timecards: LIST_PERMISSIONS.timecards,
  facilities: LIST_PERMISSIONS.facilities,
  contacts: LIST_PERMISSIONS.contacts,
  users: LIST_PERMISSIONS.users,
  imports: LIST_PERMISSIONS.imports,
  bizintel: LIST_PERMISSIONS.bizintel,
};

export const isStaffMenuItemVisible = (
  user: Pick<UserDetail, 'permissions' | 'is_staff'> | null | undefined,
  key: string
): boolean => {
  if (key === 'admin') return Boolean(user?.is_staff);
  const permission = STAFF_MENU_ITEM_PERMISSIONS[key];
  return !permission || hasPermission(user, permission);
};

// Staff-only capabilities (seeded only into Django auth, see
// backend accounts/services/portal_auth_service.py).
export const INGEST_FILE_CREATE_PERMISSION = 'create__ingestion__ingestfile';
export const INGEST_FILE_UPDATE_PERMISSION = 'update__ingestion__ingestfile';
export const INGEST_FILE_DELETE_PERMISSION = 'delete__ingestion__ingestfile';
export const INGEST_ROW_UPDATE_PERMISSION = 'update__ingestion__ingestrow';
export const SIGNATURE_REQUEST_UPDATE_PERMISSION = 'update__signatures__signaturerequest';

// Shared codes reused by staff-portal gates.
export const ASSIGNMENT_UPDATE_PERMISSION = 'update__assignments__assignment';
export const TIMECARD_READ_PERMISSION = 'read__assignments__timecard';
export const FACILITY_UPDATE_PERMISSION = 'update__healthcare_entities__healthcarefacility';
// Detail pages open on read alone; the menus/list pages need list__.
export const FACILITY_READ_PERMISSION = 'read__healthcare_entities__healthcarefacility';

/**
 * Child document contexts resolve to their menu model for staff authorization
 * (mirrors `STAFF_CONTEXT_SCOPE` in backend `documents/permissions.py`).
 */
const STAFF_DOCUMENT_CONTEXT_SCOPE: Record<string, string> = {
  'assignments.timecardshift': 'assignments.timecard',
  'assignments.assignmentshift': 'assignments.assignment',
  'assignments.assignmentexpense': 'assignments.assignment',
  'providers.providerreferenceresponse': 'providers.providerreference',
};

/**
 * The portal code a STAFF user needs to read / download the documents of a context
 * (`providers.provider` → `read__providers__provider`). Other audiences are governed
 * by ownership and the document type's audiences, not by this code.
 */
export const staffDocumentReadPermission = (contextType: string): string => {
  const scope = STAFF_DOCUMENT_CONTEXT_SCOPE[contextType] ?? contextType;
  return `read__${scope.replace('.', '__')}`;
};
